import { create } from 'zustand';
import type { WorkshopProject, ElectricalWire, WireColor, ElectricalDevice } from '../model/types';
import { createBlankPanelProject, PRESET_CIRCUITS } from '../model/defaultCircuits';
import { workshopAudio } from '../audio/workshopAudio';

export type WorkshopSubView = 'panel' | 'schematic' | 'split';

export interface WorkshopStore {
  activeSubView: WorkshopSubView;
  project: WorkshopProject;
  activeWireColor: WireColor;
  isSimulating: boolean;
  isMuted: boolean;
  selectedPaletteItemId: string | null;
  selectedDeviceId: string | null;

  // Eylemler
  setSubView: (view: WorkshopSubView) => void;
  setActiveWireColor: (color: WireColor) => void;
  setProject: (project: WorkshopProject) => void;
  updateDeviceProp: (deviceId: string, propKey: string, value: unknown) => void;
  addWire: (wire: ElectricalWire) => void;
  removeWire: (wireId: string) => void;
  addDevice: (device: ElectricalDevice) => void;
  removeDevice: (deviceId: string) => void;
  setSelectedPaletteItem: (itemId: string | null) => void;
  setSelectedDeviceId: (deviceId: string | null) => void;
  resetCircuit: () => void;
  loadPreset: (presetId: string) => void;
  setSimulating: (simulating: boolean) => void;
  setMuted: (muted: boolean) => void;
}

export const useWorkshopStore = create<WorkshopStore>((set) => ({
  activeSubView: 'panel',
  project: createBlankPanelProject(),
  activeWireColor: '#1f2937', // Standart kumanda fazı (Siyah)
  isSimulating: true,
  isMuted: false,
  selectedPaletteItemId: null,
  selectedDeviceId: null,

  setSubView: (activeSubView) => set({ activeSubView }),
  setActiveWireColor: (activeWireColor) => set({ activeWireColor }),
  setProject: (project) => set({ project }),

  updateDeviceProp: (deviceId, propKey, value) =>
    set((state) => ({
      project: {
        ...state.project,
        devices: state.project.devices.map((dev) =>
          dev.id === deviceId
            ? { ...dev, props: { ...dev.props, [propKey]: value } }
            : dev
        ),
      },
    })),

  addWire: (wire) =>
    set((state) => ({
      project: {
        ...state.project,
        wires: [...state.project.wires, wire],
      },
    })),

  removeWire: (wireId) =>
    set((state) => ({
      project: {
        ...state.project,
        wires: state.project.wires.filter((w) => w.id !== wireId),
      },
    })),

  addDevice: (device) => {
    workshopAudio.playBreakerToggle();
    set((state) => {
      const exists = state.project.devices.some((d) => d.id === device.id);
      return {
        project: {
          ...state.project,
          devices: exists
            ? state.project.devices.map((d) => (d.id === device.id ? device : d))
            : [...state.project.devices, device],
        },
        selectedPaletteItemId: null,
      };
    });
  },

  removeDevice: (deviceId) => {
    workshopAudio.playBreakerToggle();
    set((state) => ({
      project: {
        ...state.project,
        devices: state.project.devices.filter((d) => d.id !== deviceId),
        wires: state.project.wires.filter(
          (w) => w.from.deviceId !== deviceId && w.to.deviceId !== deviceId
        ),
      },
      selectedDeviceId: state.selectedDeviceId === deviceId ? null : state.selectedDeviceId,
    }));
  },

  setSelectedPaletteItem: (selectedPaletteItemId) => set({ selectedPaletteItemId }),

  setSelectedDeviceId: (selectedDeviceId) => set({ selectedDeviceId }),

  resetCircuit: () =>
    set({
      project: createBlankPanelProject(),
      selectedDeviceId: null,
      selectedPaletteItemId: null,
    }),

  loadPreset: (presetId) => {
    const preset = PRESET_CIRCUITS.find((p) => p.id === presetId);
    if (preset) {
      set({
        project: preset.factory(),
        selectedDeviceId: null,
        selectedPaletteItemId: null,
      });
    }
  },

  setSimulating: (isSimulating) => set({ isSimulating }),

  setMuted: (isMuted) => {
    workshopAudio.setMuted(isMuted);
    set({ isMuted });
  },
}));
